import React, { useState, useEffect } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import { useNavigate } from 'react-router-dom';
import EvaluationTemplateSelectBox from './Shards/EvaluationTemplateSelectBox';
import { usePopupClose } from '../Utils/usePopupClose';

/**
 * AssignEvaluation Component
 * Popup for assigning evaluations to candidates or talent
 */
const AssignEvaluation = ({ 
  isOpen, 
  onClose,
  onAssignSuccess, // Callback function to call after successful assignment
  contextType, // 'candidate' | 'talent' - determines the assignment context
  jobId = null, // Job ID to fetch evaluations for (required for candidate context)
  candidateId = null, // Candidate ID to assign the evaluation to (required for candidate context)
  talentId = null // Talent ID - for candidates: associated talent, for talent: direct assignment target
}) => {
  // State for evaluations data
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for selected evaluation and assignment process
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignError, setAssignError] = useState(null);
  
  // Hooks for session and navigation
  const { user, logout } = useSession();
  const navigate = useNavigate();
  
  // Hook for popup close functionality (ESC key)
  const popupRef = usePopupClose(isOpen, onClose);

  // Function to fetch evaluations from the API
  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Build URL with context-specific parameters
      const url = new URL(API_ENDPOINTS.GET_ASSIGNABLE_EVALUATIONS);
      url.searchParams.append('context', contextType);
      url.searchParams.append('tid', talentId);
      
      if (contextType === 'candidate') {
        url.searchParams.append('jid', jobId);
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      // Check if the response is ok
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data with error handling for empty responses
      let data = [];
      try {
        data = await response.json();
        console.log('Evaluations data for assignment:', data);
      } catch (parseError) {
        console.log('Empty or invalid response received for evaluations');
        data = [];
      }
      
      // Update evaluations state
      setEvaluations(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching evaluations for assignment:', err);
      console.log('Error status code:', err.status);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setError(err.message || 'Failed to fetch evaluations');
    } finally {
      setLoading(false);
    }
  };

  // Handle evaluation selection
  const handleEvaluationSelect = (evaluation) => {
    setSelectedEvaluation(evaluation);
    // Clear any previous assign errors when selecting a new evaluation
    setAssignError(null);
  };
  
  // Handle assign button click
  const handleAssign = async () => {
    if (!selectedEvaluation) {
      setAssignError('Please select an evaluation to assign');
      return;
    }
    
    try {
      setIsAssigning(true);
      setAssignError(null);
      
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Prepare request payload based on context
      const payload = {
        evaluation_id: selectedEvaluation.evaluation_id,
        context: contextType,
        talent_id: talentId
      };
      
      // Add candidate_id only for candidate context
      if (contextType === 'candidate') {
        payload.candidate_id = candidateId;
      }
      
      console.log('Assigning evaluation with payload:', payload);
      
      // Make POST request to assign evaluation
      const response = await fetch(API_ENDPOINTS.ASSIGN_EVALUATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify(payload)
      });
      
      // Check if the response is ok
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse response
      const data = await response.json();
      console.log('Assignment response:', data);
      
      // Call the success callback to refresh the evaluations list
      if (onAssignSuccess) {
        onAssignSuccess();
      }
      
      // Close the popup
      onClose();
      
    } catch (err) {
      console.error('Error assigning evaluation:', err);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setAssignError(err.message || 'Failed to assign evaluation');
    } finally {
      setIsAssigning(false);
    }
  };

  // Fetch evaluations when component mounts and relevant props change
  useEffect(() => {
    if (isOpen && talentId) {
      fetchEvaluations();
    }
  }, [isOpen, contextType, talentId, jobId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div 
          ref={popupRef}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 relative z-10"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center pb-3 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">
              Assign Evaluation
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content - List of evaluations */}
          <div className="py-4">
            <p className="text-gray-700 mb-6">
              Select an evaluation to assign to the {contextType === 'candidate' ? 'candidate' : 'talent'}:
            </p>
            
            {loading && (
              <div className="py-8 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
                <p className="text-gray-600">Loading evaluations...</p>
              </div>
            )}
            
            {!loading && error && (
              <div className="py-8 flex flex-col items-center justify-center">
                <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-3 w-full">
                  <p className="font-medium text-sm">Error</p>
                  <p className="text-xs mt-1">{error}</p>
                </div>
                <button 
                  onClick={() => fetchEvaluations()}
                  className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Try Again
                </button>
              </div>
            )}
            
            {!loading && !error && evaluations.length === 0 && (
              <div className="py-8 flex flex-col items-center justify-center">
                <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-gray-500 font-medium mb-2">No Evaluations Found</p>
                <p className="text-gray-400 text-sm max-w-md text-center">
                  {contextType === 'candidate' 
                    ? 'No evaluations are available for this job. Please create an evaluation first.'
                    : 'No evaluations are available for this talent. Please create an evaluation first.'
                  }
                </p>
              </div>
            )}
            
            {!loading && !error && evaluations.length > 0 && (
              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-3">
                  {evaluations.map((evaluation, index) => (
                    <EvaluationTemplateSelectBox
                      key={index}
                      evaluation={evaluation}
                      isSelected={selectedEvaluation && selectedEvaluation.evaluation_id === evaluation.evaluation_id}
                      onClick={handleEvaluationSelect}
                      index={index}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Error message for assignment */}
          {assignError && (
            <div className="mt-4 bg-red-100 text-red-700 p-3 rounded-lg">
              <p className="font-medium text-sm">Error</p>
              <p className="text-xs mt-1">{assignError}</p>
            </div>
          )}
          
          {/* Footer */}
          <div className="pt-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded hover:bg-gray-300 transition-colors"
              disabled={isAssigning}
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              className={`px-4 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors ${!selectedEvaluation || isAssigning ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!selectedEvaluation || isAssigning}
            >
              {isAssigning ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Assigning...
                </>
              ) : (
                'Assign'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignEvaluation;
